import type Model from '@ember-data/model';
import type Store from '@ember-data/store';
import type ModelRegistry from '@ember-data/types/registries/model';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { EmberChangeset } from 'ember-changeset';
import or from 'ember-truth-helpers/helpers/or';
import capitalize from 'parog-web/helpers/capitalize';
import MedicModel from 'parog-web/models/medic';
import type Patient from 'parog-web/models/patient';
import Dropdown from 'ui-kit/components/dropdown';

interface Signature {
  Args: {
    labelName: string;
    isReadOnly?: boolean;
    changeset: EmberChangeset;
    propertyName: string;
    patient: Patient;
  };
}

interface Responsible {
  id: string;
  name: string;
}

export default class PlanificationResponsibleDropdownComponent extends Component<Signature> {
  @service declare store: Store;

  declare creator: keyof ModelRegistry | undefined;

  get possibleResponsibles(): Responsible[] {
    const medics = this.store.peekAll('medic');
    const medicsFilters = medics.filter(
      medic => !MedicModel.nonDisplayableRoles.includes(medic.role)
    );
    const sortedMedicsByFirstName = medicsFilters
      .slice()
      .sort((a, b) =>
        a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase())
      );

    const sortedMedics = sortedMedicsByFirstName.sort((a, b) =>
      a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase())
    );

    const responsibles = sortedMedics.map(creator => ({
      id: this.#computeCustomUuid(creator),
      name: creator.fullName
    }));

    responsibles.push({
      id: this.#computeCustomUuid(undefined),
      name: 'Autre'
    });

    return responsibles;
  }

  get selectedResponsible() {
    const responsible = this.#changeset.get(this.#propertyName);
    return this.#computeCustomUuid(responsible?.content || responsible);
  }

  get #changeset() {
    return this.args.changeset;
  }

  get #propertyName() {
    return this.args.propertyName;
  }

  onChange = (customUuid: string | string[]) => {
    if (Array.isArray(customUuid)) {
      throw new Error('onChange: multiple selection is not supported here');
    }
    this.#changeset.set(
      this.#propertyName,
      this.#retrieveResponsible(customUuid)
    );
  };

  #computeCustomUuid(creator: MedicModel | undefined): string {
    if (creator) {
      return `${(creator?.constructor as typeof Model).modelName}-${
        creator?.id
      }`;
    }
    return 'undefined-undefined';
  }

  #retrieveResponsible(customUuid: string) {
    const creatorInfos = customUuid.split('-');

    const creator = this.store.peekRecord(
      creatorInfos[0] as keyof ModelRegistry,
      creatorInfos[1]
    );
    return creator;
  }

  <template>
    <Dropdown
      @selected={{this.selectedResponsible}}
      @isReadOnly={{@isReadOnly}}
      @labelName={{or @labelName 'Responsable'}}
      @onChange={{this.onChange}}
      data-test-responsible-dropdown
      as |dropdown|
    >
      {{#each this.possibleResponsibles as |responsible|}}
        <dropdown.Item
          @key={{responsible.id}}
          @value={{capitalize responsible.name}}
        />
      {{/each}}
    </Dropdown>
  </template>
}
